import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { errMsg } from "./apiError";
import { Loader2, Plus, Trash2, AlertTriangle, CheckCircle2, Wallet, TrendingDown, PiggyBank, CalendarClock, Pencil } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059] w-full";
const empty = { libelle: "", montant: "", statut: "prevue", date_echeance: "", date_paiement: "", note: "" };

const fmt = (n) => `${Number(n || 0).toLocaleString("fr-FR")} $US`;

export default function AdminFinances() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/finances`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Erreur de chargement des finances"));
  }, []);
  useEffect(load, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      libelle: form.libelle,
      montant: parseFloat(form.montant) || 0,
      statut: form.statut,
      date_echeance: form.date_echeance || null,
      date_paiement: form.date_paiement || null,
      note: form.note || null,
    };
    try {
      if (editing) {
        await axios.patch(`${API}/admin/depenses/${editing}`, payload, { withCredentials: true });
        toast.success("Dépense mise à jour");
      } else {
        await axios.post(`${API}/admin/depenses`, payload, { withCredentials: true });
        toast.success("Dépense enregistrée");
      }
      setForm(empty);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Erreur"));
    } finally {
      setLoading(false);
    }
  };

  const edit = (d) => {
    setEditing(d.id);
    setForm({
      libelle: d.libelle, montant: String(d.montant), statut: d.statut,
      date_echeance: d.date_echeance || "", date_paiement: d.date_paiement || "", note: d.note || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const marquerFaite = async (d) => {
    await axios.patch(`${API}/admin/depenses/${d.id}`, {
      libelle: d.libelle, montant: d.montant, statut: "faite",
      date_echeance: d.date_echeance || null, date_paiement: new Date().toISOString().slice(0, 10), note: d.note || null,
    }, { withCredentials: true });
    toast.success("Dépense marquée comme payée");
    load();
  };

  const remove = async (d) => {
    if (!window.confirm(`Supprimer la dépense « ${d.libelle} » ?`)) return;
    await axios.delete(`${API}/admin/depenses/${d.id}`, { withCredentials: true });
    toast.success("Dépense supprimée");
    load();
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-[#C5A059]" /></div>;

  const cartes = [
    { label: "Total encaissé", detail: `${data.inscrits_payes} inscription(s) payée(s)`, valeur: fmt(data.encaisse), icon: Wallet, couleur: "text-[#C5A059]" },
    { label: "Dépenses effectuées", detail: `${data.depenses_faites.length} dépense(s)`, valeur: fmt(data.depense), icon: TrendingDown, couleur: "text-red-300" },
    { label: "Solde disponible", detail: "Encaissé moins dépensé", valeur: fmt(data.solde), icon: PiggyBank, couleur: data.solde >= 0 ? "text-emerald-300" : "text-red-400" },
    { label: "Dépenses prévues", detail: `${data.depenses_prevues.length} à venir`, valeur: fmt(data.prevu_total), icon: CalendarClock, couleur: "text-amber-300" },
  ];

  return (
    <div data-testid="admin-finances" className="space-y-10">
      {/* Cartes de balance */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cartes.map((c) => (
          <div key={c.label} className="border border-[#27272A] bg-[#121214] p-6" data-testid={`finance-card-${c.label.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#71717A]">{c.label}</p>
              <c.icon size={16} className={c.couleur} />
            </div>
            <p className={`font-display text-3xl font-bold mt-3 ${c.couleur}`}>{c.valeur}</p>
            <p className="text-[11px] text-[#71717A] mt-1">{c.detail}</p>
          </div>
        ))}
      </div>

      {/* Alerte manque de fonds */}
      {data.manque > 0 && (
        <div className="border border-red-400/50 bg-red-400/10 p-5 flex items-start gap-4" data-testid="finance-manque-alerte">
          <AlertTriangle size={20} className="text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-red-300 font-semibold text-sm uppercase tracking-wider">Manque de fonds — {fmt(data.manque)}</p>
            <p className="text-xs text-[#A1A1AA] mt-1 leading-relaxed">
              Le solde disponible ne couvre pas toutes les dépenses prévues. Les dépenses signalées en rouge ci-dessous ne sont pas couvertes.
            </p>
          </div>
        </div>
      )}
      {data.manque === 0 && data.depenses_prevues.length > 0 && (
        <div className="border border-emerald-400/40 bg-emerald-400/5 p-4 flex items-center gap-3" data-testid="finance-couvert-ok">
          <CheckCircle2 size={16} className="text-emerald-300" />
          <p className="text-emerald-300 text-xs uppercase tracking-wider">Toutes les dépenses prévues sont couvertes par le solde disponible</p>
        </div>
      )}

      {/* Formulaire dépense */}
      <form onSubmit={submit} className="border border-[#C5A059]/40 bg-[#121214] p-6 lg:p-8" data-testid="depense-form">
        <p className="text-xs uppercase tracking-[0.25em] text-[#C5A059] mb-6">{editing ? "Modifier la dépense" : "Nouvelle dépense"}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <input required value={form.libelle} onChange={set("libelle")} placeholder="Libellé (ex : Location terrain) *" data-testid="depense-libelle" className={inputCls} />
          <input required type="number" min="0" step="any" value={form.montant} onChange={set("montant")} placeholder="Montant $US *" data-testid="depense-montant" className={inputCls} />
          <select value={form.statut} onChange={set("statut")} data-testid="depense-statut" className={inputCls}>
            <option value="prevue">À venir (prévue)</option>
            <option value="faite">Déjà payée</option>
          </select>
          {form.statut === "prevue" ? (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Date d'échéance</label>
              <input type="date" value={form.date_echeance} onChange={set("date_echeance")} data-testid="depense-echeance" className={`${inputCls} [color-scheme:dark]`} />
            </div>
          ) : (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Date de paiement</label>
              <input type="date" value={form.date_paiement} onChange={set("date_paiement")} data-testid="depense-date-paiement" className={`${inputCls} [color-scheme:dark]`} />
            </div>
          )}
          <input value={form.note} onChange={set("note")} placeholder="Note (optionnel)" data-testid="depense-note" className={inputCls} />
        </div>
        <div className="flex items-center gap-4 mt-6">
          <button type="submit" disabled={loading} data-testid="depense-submit"
            className="inline-flex items-center gap-3 bg-[#C5A059] text-black px-8 py-3 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors disabled:opacity-60">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
            {editing ? "Enregistrer" : "Ajouter la dépense"}
          </button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(empty); }} data-testid="depense-cancel"
              className="text-xs uppercase tracking-widest text-[#71717A] hover:text-white transition-colors">
              Annuler
            </button>
          )}
        </div>
      </form>

      {/* Grille des dépenses prévues */}
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-4">Dépenses à venir ({data.depenses_prevues.length})</p>
        <div className="border border-[#27272A] divide-y divide-[#27272A]">
          {data.depenses_prevues.length === 0 && <p className="px-5 py-8 text-center text-[#71717A] text-sm">Aucune dépense prévue.</p>}
          {data.depenses_prevues.map((d) => (
            <div key={d.id} data-testid={`depense-prevue-${d.id.slice(0, 8)}`} className="px-5 py-4 flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <p className="font-medium">{d.libelle}</p>
                <p className="text-xs text-[#71717A] mt-0.5">
                  {d.date_echeance ? `Échéance : ${d.date_echeance}` : "Sans échéance"}{d.note ? ` — ${d.note}` : ""}
                </p>
              </div>
              {d.couverte ? (
                <span className="text-[10px] uppercase tracking-wider px-2 py-1 border border-emerald-400/50 text-emerald-300" data-testid={`depense-couverte-${d.id.slice(0, 8)}`}>Couverte</span>
              ) : (
                <span className="text-[10px] uppercase tracking-wider px-2 py-1 border border-red-400/50 text-red-300 flex items-center gap-1.5" data-testid={`depense-non-couverte-${d.id.slice(0, 8)}`}>
                  <AlertTriangle size={11} /> Fonds insuffisants
                </span>
              )}
              <span className="font-display font-bold text-amber-300 whitespace-nowrap">{fmt(d.montant)}</span>
              <button onClick={() => marquerFaite(d)} data-testid={`depense-payee-${d.id.slice(0, 8)}`} title="Marquer comme payée"
                className="text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-[#C5A059]/60 text-[#C5A059] hover:bg-[#C5A059] hover:text-black transition-colors">
                Payée
              </button>
              <button onClick={() => edit(d)} data-testid={`depense-edit-${d.id.slice(0, 8)}`} className="text-[#71717A] hover:text-[#C5A059] transition-colors" aria-label="Modifier"><Pencil size={14} /></button>
              <button onClick={() => remove(d)} data-testid={`depense-delete-${d.id.slice(0, 8)}`} className="text-[#71717A] hover:text-red-400 transition-colors" aria-label="Supprimer"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Grille des dépenses faites */}
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-4">Dépenses déjà payées ({data.depenses_faites.length})</p>
        <div className="border border-[#27272A] divide-y divide-[#27272A]">
          {data.depenses_faites.length === 0 && <p className="px-5 py-8 text-center text-[#71717A] text-sm">Aucune dépense enregistrée.</p>}
          {data.depenses_faites.map((d) => (
            <div key={d.id} data-testid={`depense-faite-${d.id.slice(0, 8)}`} className="px-5 py-4 flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <p className="font-medium">{d.libelle}</p>
                <p className="text-xs text-[#71717A] mt-0.5">
                  {d.date_paiement ? `Payée le ${d.date_paiement}` : "Payée"}{d.note ? ` — ${d.note}` : ""}
                </p>
              </div>
              <span className="text-[10px] uppercase tracking-wider px-2 py-1 border border-emerald-400/50 text-emerald-300">Payée</span>
              <span className="font-display font-bold text-red-300 whitespace-nowrap">-{fmt(d.montant)}</span>
              <button onClick={() => edit(d)} data-testid={`depense-faite-edit-${d.id.slice(0, 8)}`} className="text-[#71717A] hover:text-[#C5A059] transition-colors" aria-label="Modifier"><Pencil size={14} /></button>
              <button onClick={() => remove(d)} data-testid={`depense-faite-delete-${d.id.slice(0, 8)}`} className="text-[#71717A] hover:text-red-400 transition-colors" aria-label="Supprimer"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
